import { FileText, ArrowRight } from "lucide-react";
import React from "react";
import { useNavigate } from "@tanstack/react-router";
import { Card, CardContent } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { PdfIcon } from "../icons/PdfIcon";
import { WordIcon } from "../icons/WordIcon";
import { IJob, JobStatus } from "@/hooks/useJobs";
import type { Collaborator, SharedJob } from "@/components/Sharing/types";
import { useFeatureFlags } from "@/contexts/FeatureFlagContext";
import { useAuth } from "@/contexts/AuthContext";
import { useAnalytics } from "@/hooks/useAnalytics";
import { toast } from "@/utils/toast";
import { CollaboratorAvatars } from "@/components/Sharing/CollaboratorAvatars";
import { NewBadge } from "@/components/Sharing/NewBadge";
import { JobTitle } from "./JobTitle";
import { JobAuthor } from "./JobAuthor";
import {
  resolveDisplayAuthor,
  resolveDisplayTitle,
} from "@/utils/manuscriptMetadata";
import { JobDate } from "./JobDate";
import { JobBadge } from "./JobBadge";
import { SummaryLengthBadge } from "./SummaryLengthBadge";
import { DeletionDate } from "./DeletionDate";

export type JobCardVariant = "owned" | "shared";

export type JobCardProps =
  { variant?: "owned"; job: IJob } | { variant: "shared"; job: SharedJob };

const fileIconMap: {
  [key: string]: React.ComponentType<{ className?: string }>;
} = {
  pdf: PdfIcon,
  docx: WordIcon,
};

const getToastMessageForStatus = (status: JobStatus): string => {
  switch (status) {
    case JobStatus.IN_PROGRESS:
    case JobStatus.QUEUED:
    case JobStatus.PENDING_UPLOAD:
      return "Please wait for summary generation to complete";
    case JobStatus.ERROR:
      return "Summary generation failed";
    default:
      return "";
  }
};

const FileIcon = ({ fileName }: { fileName: string }) => {
  const extension = fileName.split(".").pop()?.toLowerCase() || "";
  const IconComponent = fileIconMap[extension] || FileText;

  const icon =
    IconComponent !== FileText ? (
      <IconComponent className="h-[1rem] w-[1rem]" />
    ) : (
      <IconComponent
        className="h-[1rem] w-[1rem] text-gray-500"
        data-testid="file-icon-fallback"
      />
    );

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div>{icon}</div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{fileName}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

interface ResolvedJob {
  variant: JobCardVariant;
  jobId: string;
  fileName: string;
  displayTitle: string;
  displayAuthor: string;
  desiredWordCount: number;
  date: Date;
  isCompleted: boolean;
  status?: JobStatus;
  displayStatus?: JobStatus;
  markedForDeletion: boolean;
  ownerEmail?: string;
  collaborators?: Collaborator[];
  hasOpened?: boolean;
}

const resolveJob = (props: JobCardProps): ResolvedJob => {
  if (props.variant === "shared") {
    return {
      variant: "shared",
      jobId: props.job.jobId,
      fileName: props.job.fileName,
      displayTitle: resolveDisplayTitle(
        props.job.manuscriptTitle,
        props.job.fileName,
      ),
      displayAuthor: resolveDisplayAuthor(props.job.manuscriptAuthor),
      desiredWordCount: props.job.desiredWordCount,
      date: props.job.sharedAt,
      isCompleted: true,
      markedForDeletion: false,
      ownerEmail: props.job.ownerEmail,
      collaborators: props.job.collaborators,
      hasOpened: props.job.hasOpened,
    };
  }
  return {
    variant: "owned",
    jobId: props.job.jobId,
    fileName: props.job.fileName,
    displayTitle: resolveDisplayTitle(
      props.job.manuscriptTitle,
      props.job.fileName,
    ),
    displayAuthor: resolveDisplayAuthor(props.job.manuscriptAuthor),
    desiredWordCount: props.job.desiredWordCount,
    date: props.job.uploadedAt,
    isCompleted: props.job.status === JobStatus.COMPLETED,
    status: props.job.status,
    displayStatus: props.job.displayStatus,
    markedForDeletion: props.job.markedForDeletion,
  };
};

const buildSharedAvatars = (
  ownerEmail: string | undefined,
  collaborators: Collaborator[] | undefined,
  viewerEmail: string,
): Collaborator[] => {
  const others = (collaborators ?? []).filter((c) => c.email !== viewerEmail);
  return ownerEmail ? [{ email: ownerEmail }, ...others] : others;
};

export const JobCard = (props: JobCardProps) => {
  const navigate = useNavigate({ from: "/home" });
  const { features } = useFeatureFlags();
  const { logEvent } = useAnalytics();
  const { authUser } = useAuth();

  const job = resolveJob(props);
  const isShared = job.variant === "shared";
  const isManuscriptDeletionEnabled = features.manuscriptDeletion;
  const { isCompleted, displayStatus } = job;
  const isAcquired = job.markedForDeletion === false;
  const viewerEmail = (authUser?.email ?? "").toLowerCase();
  const sharedAvatars = isShared
    ? buildSharedAvatars(job.ownerEmail, job.collaborators, viewerEmail)
    : [];
  const showNewBadge = isShared && job.hasOpened === false;

  const handleCardClick = () => {
    logEvent("job_card_clicked", { jobStatus: job.status });

    if (isCompleted) {
      navigate({ to: "/jobs/$jobId", params: { jobId: job.jobId } });
    } else if (displayStatus) {
      const message = getToastMessageForStatus(displayStatus);
      if (message) {
        if (displayStatus === JobStatus.ERROR) {
          toast.error(message);
        } else {
          toast.loading(message);
        }
      }
    }
  };

  const cardClassName = `h-[4rem] py-[0.125rem] content-center px-[1rem] my-[0.375rem] rounded-2xl shadow-none backdrop-blur/lg min-w-[20rem] ${
    isCompleted ? "group cursor-pointer" : ""
  }`;

  return (
    <Card
      className={cardClassName}
      onClick={handleCardClick}
      data-testid="job-card"
      data-status={job.status}
    >
      <CardContent className="flex flex-row items-center justify-between p-0 gap-[0.75rem] text-sm leading-5 select-none">
        <FileIcon fileName={job.fileName} />
        <JobTitle title={job.displayTitle} />
        <JobAuthor author={job.displayAuthor} />
        <SummaryLengthBadge desiredWordCount={job.desiredWordCount} />
        <JobDate uploadedAt={job.date} />
        {!isShared && isManuscriptDeletionEnabled && (
          <DeletionDate uploadedAt={job.date} isAcquired={isAcquired} />
        )}
        {!isShared && displayStatus && <JobBadge status={displayStatus} />}
        {isShared && (
          <div className="flex items-center justify-end w-[3rem] shrink-0">
            {showNewBadge && <NewBadge />}
          </div>
        )}
        {isShared && (
          <div className="flex items-center justify-start w-[8rem] shrink-0">
            <CollaboratorAvatars collaborators={sharedAvatars} />
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <span
            className={`text-xs font-medium transition-opacity ${
              isCompleted ? "opacity-0 group-hover:opacity-100" : "opacity-0"
            }`}
          >
            View
          </span>
          <ArrowRight
            className={`w-[1rem] h-[1rem] transition-transform ${
              isCompleted
                ? "text-foreground group-hover:translate-x-0.5"
                : "text-muted-foreground opacity-50"
            }`}
            data-testid="arrow-icon"
          />
        </div>
      </CardContent>
    </Card>
  );
};
